import React, { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PermissionDenied from '@/pages/PermissionDenied';
import { routes } from '@/core/router/routes';
import { Title } from '@/components/atoms/Title';
import { Text } from '@/components/atoms/Text';
import RouterNavigationTabs from '@/components/organisms/RouterNavigationTabs';
import { Table } from '@/components/atoms/Table';
import RestaurantSelector from '@/components/molecules/RestaurantSelector';
import { Button } from '@/components/atoms/Button';
import { getAssertedUserBrandId } from '@/redux/user';
import OptionsModal from '@/pages/Brand/Customer/OptionsModal';
import { SvyIcon } from '@/components/savory/SvyIcon';
import { useCustomersTableVM } from '@/pages/Brand/Customer/Customer.viewmodel';
import { DropDown } from '@/components/atoms/DropDown';
import { useCustomersListVM } from './List.viewmodel';
import CreateCustomerModal from './CreateCustomerModal';
import {
    Container,
    HeaderContainer,
    Header,
    HeaderTitle,
    HeaderRight,
    Subtitle,
    ActionsContainer,
    OptionsButton,
    ExportButton,
} from './List.style';
import { ImportCustomersModal } from './ImportCustomersModal';

const ListCustomer: React.FunctionComponent<React.PropsWithChildren> = () => {
    const tabRoutes = React.useMemo(() => routes.filter(({ tab }) => tab === 'customers'), []);
    const brandId = useSelector(getAssertedUserBrandId);

    const [tableKey, setTableKey] = useState(0);
    const handleImportSuccess = useCallback(() => setTableKey((prev) => prev + 1), []);

    const {
        shouldUseGuestHierarchy,
        onHideCreateModal,
        handleImport,
        handleExportXlsx,
        handleExportCsv,
        showImportModal,
        handleCloseImportModal,
        isExporting,
    } = useCustomersListVM({
        brandId,
    });

    const exportItems = useMemo(
        () => [
            { value: 'csv', label: 'CSV', onClick: () => handleExportCsv() },
            { value: 'xlsx', label: 'XLSX', onClick: () => handleExportXlsx() },
        ],
        [handleExportCsv, handleExportXlsx],
    );
    const {
        headers,
        rows,
        isLoading,
        handleRowClick,
        fetchData,
        fetchDataTotalCount,
        buttonLabel,
        selectedRestaurantId,
        restaurants,
        handleRestaurantChange,
        defaultHiddenColumns,
        showOptionsModal,
        handleOpenOptionModal,
        handleCloseOptionModal,
        customerFetchOptions,
        handleCustomerFetchOptionsChange,
    } = useCustomersTableVM({ brandId });

    return shouldUseGuestHierarchy ? (
        <RouterNavigationTabs routes={tabRoutes}>
            <CreateCustomerModal title="customer.create.modalTitle" isOpen onHide={onHideCreateModal} />
            <ImportCustomersModal
                isOpen={showImportModal}
                onHide={handleCloseImportModal}
                brandId={brandId}
                onImportSuccess={handleImportSuccess}
            />
            <OptionsModal
                isOpen={showOptionsModal}
                onHide={handleCloseOptionModal}
                brandId={brandId}
                customerFetchOptions={customerFetchOptions}
                handleCustomerFetchOptionsChange={handleCustomerFetchOptionsChange}
            />
            <Container>
                <ActionsContainer>
                    <Button buttonType="primary" onClick={handleImport}>
                        <FormattedMessage id="brand.customers.action.import" />
                    </Button>
                    <DropDown
                        items={exportItems}
                        type="text"
                        headerLabel="brand.customers.action.export"
                        button={
                            <ExportButton buttonType="primary" disabled={isExporting}>
                                <SvyIcon icon="download-line" />
                                <FormattedMessage id="brand.customers.action.export" />
                                <SvyIcon icon="arrow-down-s-line" />
                            </ExportButton>
                        }
                        isUnstyledButton
                    />
                </ActionsContainer>
                <HeaderContainer>
                    <Header>
                        <HeaderTitle>
                            <Title text="brand.customers.page.title" />
                            <Subtitle>
                                <Text text="brand.customers.page.description" />
                            </Subtitle>
                        </HeaderTitle>
                        <HeaderRight>
                            <OptionsButton onClick={handleOpenOptionModal} buttonType="secondary">
                                <SvyIcon icon="filter-line" />
                                <FormattedMessage id="customers.options" />
                            </OptionsButton>
                            {restaurants && (
                                <RestaurantSelector
                                    buttonLabel={buttonLabel}
                                    restaurants={restaurants}
                                    selected={selectedRestaurantId}
                                    onChange={handleRestaurantChange}
                                    headerLabel="orderList.filters.restaurantHeader"
                                />
                            )}
                        </HeaderRight>
                    </Header>
                    <Table
                        key={tableKey}
                        isSearchable
                        rows={rows}
                        emptyMessageId="table.noRowsAfterFilter"
                        columns={headers}
                        hasPagination
                        hasUrlNavigation
                        isSortable
                        onRowClick={handleRowClick}
                        isLoading={isLoading}
                        fetchData={fetchData}
                        fetchDataTotalCount={fetchDataTotalCount}
                        columnSelectorId="customerList"
                        defaultHiddenColumns={defaultHiddenColumns}
                    />
                </HeaderContainer>
            </Container>
        </RouterNavigationTabs>
    ) : (
        <PermissionDenied />
    );
};

export default ListCustomer;
